import { Capacitor, registerPlugin } from '@capacitor/core';
import { API_BASE } from '../config/api';

// This is the only frontend boundary that knows custom Android plugin names.
// Screens consume normalized context items instead of content:// URIs, files
// in cache or Capacitor implementation details.
const NativeCalendar = registerPlugin('AthenaCalendar');
const NativeFiles = registerPlugin('AthenaFiles');
const NativeMedia = registerPlugin('AthenaMedia');
const NativeShare = registerPlugin('AthenaShare');

const noop = () => {};
const SAFE_ID = /^[A-Za-z0-9._:-]{1,180}$/;
const MAX_WEB_FILE_BYTES = 20 * 1024 * 1024;
const QUICK_CAPTURE_PREFIX = 'athena_quick_capture_drafts_v1:';
const QUICK_CAPTURE_KINDS = new Set(['inbox', 'open_loop', 'task']);

function isAndroidPluginAvailable(name) {
  try {
    return Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === 'android'
      && Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
}

function cleanText(value, maximum = 320) {
  return typeof value === 'string'
    ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, maximum)
    : '';
}

function cleanFileName(value) {
  const name = cleanText(value, 180).replace(/[\\/:*?"<>|]/g, '_');
  return name || 'arquivo';
}

function cleanMimeType(value) {
  const mime = cleanText(value, 120).toLowerCase();
  return /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/.test(mime)
    ? mime
    : 'application/octet-stream';
}

function cleanSize(value) {
  const size = Number(value);
  return Number.isFinite(size) && size >= 0 && size <= MAX_WEB_FILE_BYTES ? Math.floor(size) : 0;
}

function cleanTimestamp(value) {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? Math.floor(timestamp) : Date.now();
}

function safeIdentifier(value) {
  const id = cleanText(value, 180);
  return SAFE_ID.test(id) ? id : '';
}

function quickCaptureStorageKey(ownerId) {
  const safeOwner = safeIdentifier(ownerId);
  return safeOwner ? `${QUICK_CAPTURE_PREFIX}${safeOwner}` : '';
}

function cleanQuickCaptureContent(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '')
    .replace(/\r\n?/g, '\n')
    .trim()
    .slice(0, 2000);
}

function localQuickCaptureDraft({ kind, content, ownerId, idempotencyKey }) {
  const key = quickCaptureStorageKey(ownerId);
  const capture = Object.freeze({
    id: `local:${safeIdentifier(idempotencyKey) || cryptoSafeId()}`,
    kind,
    content,
    createdAt: new Date().toISOString(),
    status: 'local_draft',
  });
  if (!key || typeof localStorage === 'undefined') return capture;
  try {
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const drafts = Array.isArray(existing) ? existing.slice(-19) : [];
    localStorage.setItem(key, JSON.stringify([...drafts, capture]));
  } catch {
    // Storage is a convenience only. A user-submitted draft still remains in
    // the caller state even when an embedded WebView denies local storage.
  }
  return capture;
}

/**
 * Explicit-user-submit capability for the Phase 5 capture inbox. It is never
 * called from voice, proactive logic, lifecycle events, or a composer render.
 */
export async function saveAthenaQuickCapture({ kind, content, token, ownerId, idempotencyKey } = {}) {
  const safeKind = QUICK_CAPTURE_KINDS.has(kind) ? kind : '';
  const safeContent = cleanQuickCaptureContent(content);
  const safeKey = safeIdentifier(idempotencyKey);
  if (!safeKind) return { ok: false, status: 'quick_capture_kind_invalid' };
  if (!safeContent) return { ok: false, status: 'quick_capture_content_required' };
  if (typeof token !== 'string' || token.trim().length < 12) {
    return { ok: false, status: 'authentication_required' };
  }

  try {
    const response = await fetch(`${API_BASE}/quick-captures`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.trim()}` },
      body: JSON.stringify({
        kind: safeKind,
        content: safeContent,
        ...(safeKey ? { idempotency_key: safeKey } : {}),
      }),
    });
    if (response.ok) {
      const body = await response.json();
      return { ok: true, status: 'saved', capture: body?.capture || body };
    }
  } catch {
    // The F5 Worker may not be deployed while the UI is being tested.
  }

  return {
    ok: true,
    status: 'saved_local_draft',
    capture: localQuickCaptureDraft({ kind: safeKind, content: safeContent, ownerId, idempotencyKey: safeKey }),
  };
}

/** Removes only the logged-out account's local quick-capture fallback. */
export function clearAthenaQuickCaptureDrafts(ownerId) {
  const key = quickCaptureStorageKey(ownerId);
  if (!key || typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(key); } catch { /* noop */ }
}

function nativeAttachmentToContext(raw, fallbackSource = 'picker') {
  const attachmentId = safeIdentifier(raw?.attachmentId);
  if (!attachmentId) return null;
  const mimeType = cleanMimeType(raw?.mimeType);
  const source = ['picker', 'camera', 'gallery', 'share'].includes(raw?.source)
    ? raw.source
    : fallbackSource;
  return Object.freeze({
    id: `native:${attachmentId}`,
    kind: mimeType.startsWith('image/') ? 'image' : 'file',
    displayName: cleanFileName(raw?.name),
    mimeType,
    size: cleanSize(raw?.size),
    source: Object.freeze({
      kind: source,
      app: cleanText(raw?.sourceApp, 180),
      receivedAt: cleanTimestamp(raw?.receivedAt),
    }),
    // This token is valid only in the native private cache; it is never a URI
    // and it is intentionally not included in text sent to the model.
    localRef: Object.freeze({ provider: 'android-private-cache', attachmentId }),
    status: 'pending',
    attachmentRef: null,
  });
}

function webFileToContext(file, source = 'picker') {
  if (typeof File === 'undefined' || !(file instanceof File) || file.size <= 0 || file.size > MAX_WEB_FILE_BYTES) return null;
  const mimeType = cleanMimeType(file.type);
  const isImage = mimeType.startsWith('image/');
  let previewUrl = null;
  try {
    if (isImage) previewUrl = URL.createObjectURL(file);
  } catch {
    previewUrl = null;
  }
  return {
    id: `web:${cryptoSafeId()}`,
    kind: isImage ? 'image' : 'file',
    displayName: cleanFileName(file.name),
    mimeType,
    size: cleanSize(file.size),
    source: { kind: source, app: '', receivedAt: Date.now() },
    localRef: { provider: 'web-file', file },
    previewUrl,
    status: 'pending',
    attachmentRef: null,
  };
}

function cryptoSafeId() {
  try {
    return globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 14);
  } catch {
    return Math.random().toString(36).slice(2, 14);
  }
}

async function nativeCall(plugin, method, args = undefined) {
  try {
    if (typeof plugin?.[method] !== 'function') return null;
    return args === undefined ? await plugin[method]() : await plugin[method](args);
  } catch {
    return null;
  }
}

function chooseWebFiles({ accept = '', multiple = false, capture = false } = {}) {
  if (typeof document === 'undefined') return Promise.resolve({ cancelled: true, items: [] });
  return new Promise((resolve) => {
    const input = document.createElement('input');
    let done = false;
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;
    if (capture) input.capture = 'environment';
    const finish = (items) => {
      if (done) return;
      done = true;
      input.remove();
      resolve({ cancelled: items.length === 0, items });
    };
    input.addEventListener('change', () => finish(Array.from(input.files || [])), { once: true });
    window.setTimeout(() => {
      window.addEventListener('focus', () => window.setTimeout(() => finish([]), 0), { once: true });
      input.click();
    }, 0);
  });
}

export function isAthenaNativeCapabilityAvailable(capability) {
  const plugins = {
    calendar: 'AthenaCalendar',
    files: 'AthenaFiles',
    media: 'AthenaMedia',
    sharing: 'AthenaShare',
  };
  return Boolean(plugins[capability] && isAndroidPluginAvailable(plugins[capability]));
}

export async function getAthenaDeviceCapabilities() {
  const [calendar, files, media, sharing] = await Promise.all([
    isAthenaNativeCapabilityAvailable('calendar') ? nativeCall(NativeCalendar, 'getCapabilities') : null,
    isAthenaNativeCapabilityAvailable('files') ? nativeCall(NativeFiles, 'getCapabilities') : null,
    isAthenaNativeCapabilityAvailable('media') ? nativeCall(NativeMedia, 'getCapabilities') : null,
    isAthenaNativeCapabilityAvailable('sharing') ? nativeCall(NativeShare, 'getCapabilities') : null,
  ]);
  return Object.freeze({
    calendarRead: calendar?.calendarRead === true,
    calendarWrite: calendar?.calendarWrite === true,
    filePicker: files?.filePicker === true || !Capacitor.isNativePlatform(),
    documentOpen: files?.documentOpen === true,
    camera: media?.camera === true || !Capacitor.isNativePlatform(),
    gallery: media?.gallery === true || !Capacitor.isNativePlatform(),
    shareReceive: sharing?.shareReceive === true,
    shareSend: sharing?.shareSend === true || (typeof navigator !== 'undefined' && typeof navigator.share === 'function'),
    calendarPermission: calendar?.readPermission || 'unavailable',
    cameraPermission: media?.cameraPermission || 'unavailable',
  });
}

export async function pickAthenaDocuments({ multiple = true } = {}) {
  if (isAthenaNativeCapabilityAvailable('files')) {
    const result = await nativeCall(NativeFiles, 'pickDocuments', { multiple });
    const items = Array.isArray(result?.attachments)
      ? result.attachments.map((item) => nativeAttachmentToContext(item, 'picker')).filter(Boolean)
      : [];
    return { cancelled: result?.cancelled === true, items, errors: Array.isArray(result?.errors) ? result.errors : [] };
  }
  const selection = await chooseWebFiles({ multiple });
  return { ...selection, items: selection.items.map((file) => webFileToContext(file, 'picker')).filter(Boolean), errors: [] };
}

export async function pickAthenaImage() {
  if (isAthenaNativeCapabilityAvailable('media')) {
    const result = await nativeCall(NativeMedia, 'pickImage');
    const item = nativeAttachmentToContext(result?.attachment, 'gallery');
    return { cancelled: result?.cancelled === true, items: item ? [item] : [], status: result?.status || null };
  }
  const selection = await chooseWebFiles({ accept: 'image/*', multiple: false });
  return { ...selection, items: selection.items.map((file) => webFileToContext(file, 'gallery')).filter(Boolean) };
}

export async function captureAthenaPhoto() {
  if (isAthenaNativeCapabilityAvailable('media')) {
    const result = await nativeCall(NativeMedia, 'takePhoto');
    const item = nativeAttachmentToContext(result?.attachment, 'camera');
    return { cancelled: result?.cancelled === true, items: item ? [item] : [], status: result?.status || null };
  }
  const selection = await chooseWebFiles({ accept: 'image/*', capture: true, multiple: false });
  return { ...selection, items: selection.items.map((file) => webFileToContext(file, 'camera')).filter(Boolean) };
}

export async function discardAthenaContextItem(item) {
  if (item?.previewUrl) {
    try { URL.revokeObjectURL(item.previewUrl); } catch { /* URL can already be gone. */ }
  }
  const attachmentId = safeIdentifier(item?.localRef?.attachmentId);
  if (attachmentId && isAthenaNativeCapabilityAvailable('files')) {
    await nativeCall(NativeFiles, 'discardAttachment', { attachmentId });
  }
}

export async function openAthenaDocument(item) {
  const attachmentId = safeIdentifier(item?.localRef?.attachmentId);
  if (!attachmentId || !isAthenaNativeCapabilityAvailable('files')) return { opened: false, status: 'unavailable' };
  return (await nativeCall(NativeFiles, 'openDocument', { attachmentId })) || { opened: false, status: 'document_open_failed' };
}

export async function bindNativeDeviceContentOwner(ownerId) {
  if (!isAthenaNativeCapabilityAvailable('sharing')) return { available: false, ownerChanged: false };
  return (await nativeCall(NativeShare, 'bindOwner', { ownerId: safeIdentifier(ownerId) })) || { available: true, ownerChanged: false };
}

export async function drainNativeSharedContent(ownerId) {
  if (!isAthenaNativeCapabilityAvailable('sharing')) return { available: false, items: [] };
  const result = await nativeCall(NativeShare, 'drainIncoming', { ownerId: safeIdentifier(ownerId) });
  return {
    available: true,
    ownerChanged: result?.ownerChanged === true,
    items: Array.isArray(result?.items) ? result.items : [],
  };
}

export async function observeNativeSharedContent(listener) {
  if (!isAthenaNativeCapabilityAvailable('sharing') || typeof listener !== 'function') return noop;
  try {
    const handle = await NativeShare.addListener('sharedContent', listener);
    return () => {
      try {
        const removal = handle?.remove?.();
        if (removal?.catch) removal.catch(noop);
      } catch {
        // WebView teardown can invalidate a retained listener.
      }
    };
  } catch {
    return noop;
  }
}

export function normalizeNativeSharedContent(raw) {
  const contextId = safeIdentifier(raw?.contextId) || `share:${cryptoSafeId()}`;
  const text = cleanText(raw?.text, 16_000);
  const attachments = Array.isArray(raw?.attachments)
    ? raw.attachments.map((item) => nativeAttachmentToContext(item, 'share')).filter(Boolean)
    : [];
  if (!text && attachments.length === 0) return null;
  return Object.freeze({
    id: `shared:${contextId}`,
    kind: text ? 'shared_text' : 'shared_file',
    text,
    displayName: attachments.length ? `${attachments.length} arquivo${attachments.length === 1 ? '' : 's'} compartilhado${attachments.length === 1 ? '' : 's'}` : 'Texto compartilhado',
    source: Object.freeze({ kind: 'share', app: cleanText(raw?.sourceApp, 180), receivedAt: cleanTimestamp(raw?.receivedAt) }),
    attachments,
    status: 'pending',
  });
}

/**
 * Clears cached shared content for a specific signed-out account. Automatic
 * logout never falls back to the legacy global clear: on an old APK that
 * lacks the owner-scoped API, the next native bindOwner will safely isolate
 * stale data instead. Calling without an owner remains an explicit
 * current-user maintenance action and may use the legacy method.
 */
export async function clearNativeDeviceContent(ownerId = '') {
  if (!isAthenaNativeCapabilityAvailable('sharing')) return false;
  const safeOwnerId = safeIdentifier(ownerId);
  if (safeOwnerId) {
    const result = await nativeCall(NativeShare, 'clearPrivateContentForOwner', { ownerId: safeOwnerId });
    return result?.cleared === true || result?.ok === true;
  }
  const result = await nativeCall(NativeShare, 'clearPrivateContent');
  return result?.cleared === true;
}

export async function shareAthenaText({ text, title = 'Compartilhar com' } = {}) {
  const safeText = cleanText(text, 100_000);
  if (!safeText) return { opened: false, status: 'share_text_required' };
  if (isAthenaNativeCapabilityAvailable('sharing')) {
    return (await nativeCall(NativeShare, 'shareText', { text: safeText, title: cleanText(title, 80) })) || { opened: false, status: 'share_sheet_unavailable' };
  }
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return { opened: false, status: 'share_unavailable' };
  try {
    await navigator.share({ text: safeText, title: cleanText(title, 80) });
    return { opened: true };
  } catch {
    return { opened: false, status: 'share_cancelled' };
  }
}

export async function shareAthenaAttachment(item, title = 'Compartilhar arquivo') {
  const attachmentId = safeIdentifier(item?.localRef?.attachmentId);
  if (!attachmentId || !isAthenaNativeCapabilityAvailable('sharing')) return { opened: false, status: 'share_unavailable' };
  return (await nativeCall(NativeShare, 'shareAttachment', { attachmentId, title: cleanText(title, 80) })) || { opened: false, status: 'share_sheet_unavailable' };
}

/**
 * Moves one private Android attachment into the server-owned file record.
 *
 * The browser layer intentionally never receives a file path, content URI or
 * binary payload. It can only ask the native bridge to stream an opaque cache
 * token and receive a sanitized server reference back. This is deliberately
 * not a generic HTTP client: it is available only for the Athena Android
 * plugin and only for an attachment selected through the capability facade.
 */
export async function uploadAthenaContextItem(item, { token, projectId, sessionId } = {}) {
  const attachmentId = safeIdentifier(item?.localRef?.attachmentId);
  if (!attachmentId || item?.localRef?.provider !== 'android-private-cache') {
    return { ok: false, status: 'native_attachment_required' };
  }
  if (!isAthenaNativeCapabilityAvailable('files')) {
    return { ok: false, status: 'native_upload_unavailable' };
  }
  if (typeof token !== 'string' || token.trim().length < 12) {
    return { ok: false, status: 'upload_session_required' };
  }

  const result = await nativeCall(NativeFiles, 'uploadAttachment', {
    attachmentId,
    token: token.trim(),
    projectId: safeIdentifier(projectId),
    sessionId: safeIdentifier(sessionId),
  });
  if (!result || result.native_upload_ok !== true) {
    return { ok: false, status: cleanText(result?.status || 'native_upload_failed', 80) || 'native_upload_failed' };
  }

  // The Worker returns one persisted file record. Accept the two stable names
  // used by the transport contract, then expose only a small, safe reference
  // to the rest of the UI and the chat request.
  const rawFile = result.file || result.attachment || result;
  const fileId = safeIdentifier(rawFile?.id || rawFile?.file_id || result.file_id);
  if (!fileId) return { ok: false, status: 'native_upload_reference_missing' };
  const serverAttachmentId = safeIdentifier(result?.attachment?.id || result?.attachment_id);
  return {
    ok: true,
    status: 'uploaded',
    file: Object.freeze({
      id: fileId,
      name: cleanFileName(rawFile?.name || rawFile?.original_name || item.displayName),
      mimeType: cleanMimeType(rawFile?.mime_type || rawFile?.mimeType || item.mimeType),
      size: cleanSize(rawFile?.size ?? item.size),
      projectId: safeIdentifier(rawFile?.project_id || rawFile?.projectId || projectId),
    }),
    attachment: serverAttachmentId ? Object.freeze({ id: serverAttachmentId, fileId }) : null,
  };
}

export async function getNativeCalendars() {
  if (!isAthenaNativeCapabilityAvailable('calendar')) return { available: false, calendars: [] };
  const result = await nativeCall(NativeCalendar, 'listCalendars');
  return { available: true, ...result, calendars: Array.isArray(result?.calendars) ? result.calendars : [] };
}

export async function requestNativeCalendarAccess({ write = false } = {}) {
  if (!isAthenaNativeCapabilityAvailable('calendar')) return { available: false, status: 'unavailable' };
  const method = write ? 'requestWritePermission' : 'requestReadPermission';
  return (await nativeCall(NativeCalendar, method)) || { available: true, status: 'permission_request_failed' };
}

export async function listNativeCalendarEvents(input = {}) {
  if (!isAthenaNativeCapabilityAvailable('calendar')) return { available: false, events: [] };
  const result = await nativeCall(NativeCalendar, 'listEvents', input);
  return { available: true, ...result, events: Array.isArray(result?.events) ? result.events : [] };
}

export async function createNativeCalendarEvent(input) {
  if (!isAthenaNativeCapabilityAvailable('calendar')) return { available: false, status: 'unavailable' };
  return (await nativeCall(NativeCalendar, 'createEvent', input)) || { available: true, status: 'calendar_create_failed' };
}

export async function updateNativeCalendarEvent(input) {
  if (!isAthenaNativeCapabilityAvailable('calendar')) return { available: false, status: 'unavailable' };
  return (await nativeCall(NativeCalendar, 'updateEvent', input)) || { available: true, status: 'calendar_update_failed' };
}

export async function deleteNativeCalendarEvent(input) {
  if (!isAthenaNativeCapabilityAvailable('calendar')) return { available: false, status: 'unavailable' };
  return (await nativeCall(NativeCalendar, 'deleteEvent', input)) || { available: true, status: 'calendar_delete_failed' };
}
